/**
 * Filename builder for admin JSON exports.
 *
 * Shape: `hangarmax-{kind}_{slug}_{id8}_{YYYYMMDD}.json`
 * e.g.   `hangarmax-layout_main-hangar_a1b2c3d4_20260814.json`
 *
 * Pure — no DOM, no clock (the date is always passed in) — so it's unit-tested
 * directly in apps/admin/src/lib/__tests__/exportFileName.test.ts.
 */

const MAX_SLUG_LENGTH = 40

/** Lowercase, ASCII-safe, hyphen-separated. Falls back to 'untitled' when nothing survives. */
export function slugifyForFileName(name: string): string {
  const slug = (name ?? '')
    // NFKD splits accented letters into base + combining mark; dropping the
    // non-ASCII half then leaves "Ü" as "u" rather than a hyphen.
    .normalize('NFKD')
    .toLowerCase()
    // eslint-disable-next-line no-control-regex -- matching the ASCII range is the point
    .replace(/[^\x00-\x7f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG_LENGTH)
    // A mid-word truncation can leave a trailing hyphen — trim it again.
    .replace(/-+$/g, '')

  return slug || 'untitled'
}

function dateStamp(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}${m}${d}`
}

export function buildExportFileName(
  kind: 'layout' | 'location',
  name: string,
  id: string,
  date: Date,
): string {
  return `hangarmax-${kind}_${slugifyForFileName(name)}_${(id ?? '').slice(0, 8)}_${dateStamp(date)}.json`
}
